import { useStore } from "./store.service";
const store = useStore();

// Constants
// read from store config
const ACCESS_TOKEN = store.readConfig<string>('ACCESS_TOKEN_NAME') || 'ACCESS_TOKEN_NAME';
const REFRESH_TOKEN = store.readConfig<string>('REFRESH_TOKEN_NAME') || 'REFRESH_TOKEN_NAME';

/**
 * ApiService class provides HTTP client functionality with automatic token refresh
 * and centralized error handling for the application.
 */

export class ApiService {
  private readonly baseApiUrl: string;

  constructor() {
    this.baseApiUrl = process.env.VUE_APP_API_URL;
  }

  /**
   * Makes HTTP requests with automatic token refresh
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseApiUrl}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
      ...options,
    };

    const accessToken = this.getAccessToken();
    if (accessToken) {
      (config.headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
    }

    let response = await fetch(url, config);
    let json = await response.json();
    
    if (response.status === 401 && this.getRefreshToken() !== null && !endpoint.includes('/auth/refresh')) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        const accessToken = this.getAccessToken();
        
        if (accessToken) {
          (config.headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
        }

        response = await fetch(url, config);
        json = await response.json();
      }
    }

    if (!response.ok) {
      throw json;
    }

    return json as T;
  }

  /**
   * Refreshes the access token using the refresh token
   */
  private async refreshAccessToken(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${this.baseApiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (response.ok) {
        const { data } = await response.json();
        if (data.access_token) {
          this.setAccessToken(data.access_token);

          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // HTTP method implementations
  public get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  public post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  public put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  public delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Token management methods
  private getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN);
  }

  public setAccessToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN, token);
  }

  public removeAccessToken(): void {
    localStorage.removeItem(ACCESS_TOKEN);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN);
  }

  public setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN, token);
  }

  public removeRefreshToken(): void {
    localStorage.removeItem(REFRESH_TOKEN);
  }

  public clearBothTokens(): void {
    this.removeAccessToken();
    this.removeRefreshToken();
  }
}

// Singleton instance
const apiInstance = new ApiService();

/**
 * Convenient API interface for the application
 * Provides easy access to all HTTP methods and token management
 */
export const api = {
  // HTTP methods
  get: <T>(endpoint: string): Promise<T> => 
    apiInstance.get<T>(endpoint),
  
  post: <T>(endpoint: string, data?: unknown): Promise<T> => 
    apiInstance.post<T>(endpoint, data),
  
  put: <T>(endpoint: string, data?: unknown): Promise<T> => 
    apiInstance.put<T>(endpoint, data),
  
  delete: <T>(endpoint: string): Promise<T> => 
    apiInstance.delete<T>(endpoint),

  // Token management
  setAccessToken: (token: string): void => 
    apiInstance.setAccessToken(token),

  removeAccessToken: (): void => 
    apiInstance.removeAccessToken(),

  setRefreshToken: (token: string): void => 
    apiInstance.setRefreshToken(token),
  
  removeRefreshToken: (): void => 
    apiInstance.removeRefreshToken(),

  clearBothTokens: (): void => 
    apiInstance.clearBothTokens(),
} as const;
